import React, { Fragment, useMemo } from 'react';
import {
  __,
  always,
  contains,
  filter,
  has,
  identity,
  ifElse,
  inc,
  is,
  isNil,
  lt,
  map,
  min,
  pathOr,
  pick,
  pipe,
  times,
} from 'ramda';
import { Pie } from '@vx/shape';
import { Group } from '@vx/group';
import { ParentSize } from '@vx/responsive';

import { toDegrees } from '../../../utils';
import { mapIndexed } from '../../../utils/mapIndexed';
import { renderWhenTrue } from '../../../utils/rendering';
import { TRANSPARENT } from '../../../utils/colors';
import { RADIAL_DEFAULT } from '../configs/radial.config';
import { Header } from '../header';
import { isNoData, NoData } from '../noData';
import { Chart, Container, Svg } from '../charts.styles';
import { DEFAULT_SIZE } from '../charts.constants';
import {
  Arc,
  IndicatorColor,
  IndicatorLabel,
  Label,
  LegendBox,
  LegendGroupTitle,
  LegendItem,
  LegendTitle,
  RadialLegend,
} from './radialChart.styles';
import { ARC_STATES, PIE_VALUE } from './radialChart.constants';
import parser from './radialChart.parser';
import { useChartTooltipContext } from '../../../store/ChartTooltipContext';
import { renderOpenTooltip } from '../tooltipBasic/tooltipBasic.utils';
import { RadialChartHomepageTooltip } from './homepage';
import { hoverCreator } from './homepage/radialChartHomepageTooltip.hoverCreator';
import { TooltipWithPortal } from '../tooltipWithPortal/tooltipWithPortal.component';
import { useIntl } from 'gatsby-plugin-intl';

const calcArcHoverState = (tooltip, rayIndex) => {
  if (isNil(tooltip.data)) return ARC_STATES.NORMAL;

  return rayIndex === pathOr('', ['data', 'index'], tooltip) ? ARC_STATES.ACTION_HOVER : ARC_STATES.ACTION_NO_HOVER;
};

const renderLegendItem = ({ color, label }, key) => (
  <LegendItem key={key}>
    <IndicatorColor color={color} />
    <IndicatorLabel>{label}</IndicatorLabel>
  </LegendItem>
);

const renderLegendGroup = ({ group, values }, key) => {
  const { formatMessage } = useIntl();
  return (
    <Fragment key={key}>
      <LegendGroupTitle>{formatMessage({ id: group })}</LegendGroupTitle>
      {values.map(renderLegendItem)}
    </Fragment>
  );
};

const renderLegend = ({ legendVisibility, legendStructure, gridStroke }) =>
  renderWhenTrue(() => {
    const { formatMessage } = useIntl();
    return (
      <RadialLegend>
        <LegendTitle>{formatMessage({ id: 'Key' })}</LegendTitle>
        <LegendBox gridStroke={gridStroke}>{legendStructure.map(renderLegendGroup)}</LegendBox>
      </RadialLegend>
    );
  })(legendVisibility);

const getId = ifElse(is(Number), inc, identity);

const getColors = ifElse(
  ({ rayIndex, serieData }) => contains(getId(rayIndex), serieData),
  pick(['rayIndex', 'color']),
  always({})
);

const renderGrid = (pie, { gridStroke, gridStrokeWidth }) =>
  pie.arcs.map((arc, index) => (
    <path key={index} d={pie.path(arc)} fill={TRANSPARENT} stroke={gridStroke} strokeWidth={gridStrokeWidth} />
  ));

const renderRing = ({ innerRadius, ringRadius, serie, config, tooltip, rawData }) => (data, index) => {
  const innerRingRadius = innerRadius + ringRadius * index;
  const outerRingRadius = innerRingRadius + ringRadius;

  const serieData = data[serie];
  const color = config.colors[index];
  const ringData = [
    ...times((rayIndex) => getColors({ rayIndex, serieData, color, config }), config.rays),
    ...map((rayIndex) => getColors({ rayIndex, serieData, color, config }), config.extraRays),
  ];

  const renderPie = (pie, { dataStrokeWidth, gridStroke }) => {
    return pipe(
      filter(({ data } = {}) => has('color')(data)),
      mapIndexed((arc, arcIndex) => {
        const { color, rayIndex } = arc.data;

        return (
          <Arc
            hoverState={calcArcHoverState(tooltip, rayIndex)}
            key={arcIndex}
            d={pie.path(arc)}
            fill={color}
            stroke={gridStroke}
            strokeWidth={dataStrokeWidth}
            {...tooltip.bindHoverEvents(hoverCreator({ arc, rawData, tooltip }))}
          />
        );
      })
    )(pie.arcs);
  };

  return (
    <Pie key={index} data={ringData} pieValue={PIE_VALUE} innerRadius={innerRingRadius} outerRadius={outerRingRadius}>
      {(pie) => (
        <React.Fragment key={index}>
          <Group>{renderGrid(pie, config)}</Group>
          <Group>{renderPie(pie, config)}</Group>
        </React.Fragment>
      )}
    </Pie>
  );
};

const renderLabels = () => (pie) =>
  pie.arcs.map((arc, index) => {
    const { startAngle, endAngle } = arc;
    const [centroidX, centroidY] = pie.path.centroid(arc);
    const rotation = toDegrees(startAngle + (endAngle - startAngle) / 2);
    const getValue = ifElse(lt(__, 10), (value) => `0${value}`, identity);

    return (
      <Label
        key={index}
        transform={`translate(${centroidX}, ${centroidY}) rotate(${rotation})`}
        text-anchor="middle"
        alignment-baseline="middle"
      >
        {getValue(arc.data)}
      </Label>
    );
  });

const renderRingLabels = ({ config, innerRadius, outerRadius }) => {
  const labelsData = [...times((i) => i + 1, config.rays), ...config.extraRays];

  return (
    <Pie data={labelsData} pieValue={PIE_VALUE} innerRadius={innerRadius} outerRadius={outerRadius}>
      {renderLabels()}
    </Pie>
  );
};

export const RadialChart = ({
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  data: chartData = {},
  parser: customParser,
  tabs,
  config: customConfig = {},
}) => {
  const tooltip = useChartTooltipContext();

  const { title, subtitle, data, serie, rawData } = useMemo(() => {
    const customParsedData = customParser ? customParser(chartData) : chartData;
    return chartData.series && !chartData.serie ? parser(customParsedData) : customParsedData;
  }, [customParser, chartData]);

  if (isNoData(data, serie)) return <NoData width={width} height={height} />;

  const config = {
    ...RADIAL_DEFAULT,
    ...customConfig,
  };

  return (
    <Container width={width} height={height}>
      <Header title={title} subtitle={subtitle} tabs={tabs} config={config} />
      <Chart>
        {renderOpenTooltip(() => (
          <TooltipWithPortal>
            <RadialChartHomepageTooltip />
          </TooltipWithPortal>
        ))(tooltip)}
        <ParentSize>
          {({ width: chartWidth, height: chartHeight }) => {
            if (!chartWidth || !chartHeight) return null;

            const radius = min(chartWidth, chartHeight) / 2;
            const centerY = chartHeight / 2;
            const centerX = chartWidth / 2;

            const outerRadius = radius * config.pieOuterRadius;
            const innerRadius = radius * config.pieInnerRadius;
            const ringRadius = (outerRadius - innerRadius) / data.length;
            const labelOuterRadius = radius * config.labelOuterRadius;

            return (
              <Svg>
                <rect width={chartWidth} height={chartHeight} fill={config.background} />
                <Group top={centerY} left={centerX}>
                  {data.map(
                    renderRing({
                      rawData,
                      innerRadius,
                      ringRadius,
                      serie,
                      config,
                      tooltip,
                    })
                  )}
                </Group>
                <Group top={centerY} left={centerX}>
                  {renderRingLabels({
                    innerRadius: outerRadius,
                    outerRadius: labelOuterRadius,
                    config,
                  })}
                </Group>
              </Svg>
            );
          }}
        </ParentSize>
        {renderLegend(config)}
      </Chart>
    </Container>
  );
};
